import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fetchPlayerDataBatch } from "./usePlayerDataBatch";
import { registerForBatch, unregisterFromBatch } from "@/lib/playerBatchRegistry";

const BATCH_SIZE = 20;
const PREFETCH_THRESHOLD = 5; // Prefetch when user is 5 items from end

interface UseOptimizedPlayerBatchOptions {
  /** Current visible auction seller UUIDs */
  visibleUuids: string[];
  /** All sorted auction seller UUIDs (for prefetching next batch) */
  allUuids: string[];
  /** Current visible count */
  visibleCount: number;
  /** Total available count */
  totalCount: number;
  /** Avatar size */
  size?: number;
}

/**
 * Optimized hook for loading player data with:
 * 1. Only fetching NEW UUIDs when more auctions are loaded
 * 2. Prefetching next batch in background
 * 3. Avoiding duplicate fetches
 */
export const useOptimizedPlayerBatch = ({
  visibleUuids,
  allUuids,
  visibleCount,
  totalCount,
  size = 14,
}: UseOptimizedPlayerBatchOptions) => {
  const queryClient = useQueryClient();
  const fetchedUuidsRef = useRef<Set<string>>(new Set());
  const prefetchingRef = useRef<boolean>(false);

  // Fetch only NEW UUIDs that haven't been fetched yet
  const fetchNewUuids = useCallback(async (uuids: string[]) => {
    const newUuids = uuids.filter(uuid => !fetchedUuidsRef.current.has(uuid));
    
    if (newUuids.length === 0) return;

    // Register synchronously BEFORE async fetch to prevent individual hooks from racing
    registerForBatch(newUuids);

    try {
      const results = await fetchPlayerDataBatch(newUuids, size);
      
      // Mark as fetched and update query cache
      for (const [uuid, data] of Object.entries(results)) {
        fetchedUuidsRef.current.add(uuid);
        
        if (data.name) {
          queryClient.setQueryData(['playerName', uuid], data.name);
        }
        if (data.avatar_url) {
          queryClient.setQueryData(['playerAvatar', `${uuid}_${size}`], data.avatar_url);
        }
      }
      
      // Also mark UUIDs that were fetched but had no data
      for (const uuid of newUuids) {
        fetchedUuidsRef.current.add(uuid);
      }
    } catch (error) {
      console.error('Error fetching player batch:', error);
    } finally {
      // Unregister so individual hooks can fall back if batch missed some
      unregisterFromBatch(newUuids);
    }
  }, [queryClient, size]);

  // Prefetch next batch in background
  const prefetchNextBatch = useCallback(async () => {
    if (prefetchingRef.current) return;
    if (visibleCount >= totalCount) return;
    
    // Get next batch of UUIDs
    const nextBatchStart = visibleCount;
    const nextBatchEnd = Math.min(visibleCount + BATCH_SIZE, totalCount);
    const nextUuids = allUuids.slice(nextBatchStart, nextBatchEnd);
    const newNextUuids = nextUuids.filter(uuid => !fetchedUuidsRef.current.has(uuid));
    
    if (newNextUuids.length === 0) return;

    prefetchingRef.current = true;
    
    try {
      const results = await fetchPlayerDataBatch(newNextUuids, size);
      
      for (const [uuid, data] of Object.entries(results)) {
        fetchedUuidsRef.current.add(uuid);
        
        if (data.name) {
          queryClient.setQueryData(['playerName', uuid], data.name);
        }
        if (data.avatar_url) {
          queryClient.setQueryData(['playerAvatar', `${uuid}_${size}`], data.avatar_url);
        }
      }
      
      for (const uuid of newNextUuids) {
        fetchedUuidsRef.current.add(uuid);
      }
    } catch {
      // Silent fail for prefetch
    } finally {
      prefetchingRef.current = false;
    }
  }, [allUuids, visibleCount, totalCount, queryClient, size]);

  // Fetch new visible UUIDs when they change
  useEffect(() => {
    fetchNewUuids(visibleUuids);
  }, [visibleUuids, fetchNewUuids]);

  // Prefetch next batch when approaching end
  useEffect(() => {
    const remainingItems = totalCount - visibleCount;
    const shouldPrefetch = remainingItems > 0 && remainingItems <= BATCH_SIZE + PREFETCH_THRESHOLD;
    
    if (shouldPrefetch) {
      prefetchNextBatch();
    }
  }, [visibleCount, totalCount, prefetchNextBatch]);

  // Reset fetched UUIDs when filters change (allUuids completely changes)
  useEffect(() => {
    return () => {
      // Cleanup is handled by React Query's garbage collection
    };
  }, []);

  return {
    fetchedCount: fetchedUuidsRef.current.size,
    prefetch: prefetchNextBatch,
  };
};
